import { Mutex } from "async-mutex";
import type {
  CreateLocalAudioTrackOptions as TwilioCreateLocalAudioTrackOptions,
  CreateLocalTrackOptions as TwilioCreateLocalTrackOptions,
  LocalAudioTrack as TwilioLocalAudioTrack,
  LocalParticipant as TwilioLocalParticipant,
  LocalVideoTrack as TwilioLocalVideoTrack,
} from "twilio-video";
import {
  createLocalAudioTrack as createTwilioLocalAudioTrack,
  createLocalTracks as createTwilioLocalTracks,
  createLocalVideoTrack as createTwilioLocalVideoTrack,
} from "twilio-video";
import { isDevMode } from "../../../config";
import { logger } from "../../../datadog/logger";
import { deviceIsIOS, getDeviceType, isSafari } from "../../../utils";
import {
  MediaSource,
  QualityMode,
  type MediaMetadata,
} from "../../core/room/participant/media/types";
import type { LocalAVTrack } from "../../core/types";
import { getStoredAudioVideoSettings } from "../../core/utils";
import { audioVideoSettingsByQualityMode } from "../config";

export const trackKindToMutexMap = {
  audio: new Mutex(),
  video: new Mutex(),
};

export const FAILED_TO_RESTART_TRACK_ERROR_MESSAGE = "Failed to restart track";
export const FAILED_TO_REAQUIRE_TRACK_ERROR_MESSAGE =
  "Failed to re-acquire the MediaStreamTrack";
export const IS_DEV_TRACK_SETTINGS_ENABLED_KEY =
  "is-dev-track-settings-enabled";
export const DEV_TRACK_SETTINGS_KEY = "dev-track-settings";

enum DeviceInputType {
  AUDIO_INPUT = "audioinput",
  AUDIO_OUTPUT = "audiooutput",
  VIDEO_INPUT = "videoinput",
}

function getDevTrackSettings(): {
  audioSettings?: TwilioCreateLocalAudioTrackOptions;
  videoSettings?: TwilioCreateLocalTrackOptions;
} {
  if (!isDevMode) return {};
  try {
    const isDevConnectionSettingsEnabled = !!parseInt(
      localStorage.getItem(IS_DEV_TRACK_SETTINGS_ENABLED_KEY) ?? "0",
    );
    if (!isDevConnectionSettingsEnabled) return {};
    const rawSettings = localStorage.getItem(DEV_TRACK_SETTINGS_KEY);
    if (!rawSettings) return {};
    const settings = JSON.parse(rawSettings);
    logger.debug("Using dev track settings", settings);
    return settings;
  } catch (error) {
    logger.debug("Error parsing dev track settings", {}, error as Error);
    return {};
  }
}

// https://www.twilio.com/docs/video/noise-cancellation#limitations
export const isKrispNoiseCancellationSupported = !deviceIsIOS() && !isSafari();

export function getLocalAudioTrackOpts(): TwilioCreateLocalTrackOptions {
  const { inputAudioDeviceId: deviceId } = getStoredAudioVideoSettings();
  const noiseCancellationOpts = isKrispNoiseCancellationSupported
    ? {
        noiseCancellationOptions: {
          sdkAssetsPath: "/noise-cancellation",
          vendor: "krisp",
        },
      }
    : {};
  const deviceType = getDeviceType();
  return {
    deviceId,
    echoCancellation: true,
    ...audioVideoSettingsByQualityMode[QualityMode.High][deviceType].audio[
      MediaSource.Microphone
    ],
    ...noiseCancellationOpts,
    ...getDevTrackSettings().audioSettings,
  };
}

export async function enableNoiseCancellation(track: TwilioLocalAudioTrack) {
  if (!track.noiseCancellation || track.noiseCancellation.isEnabled) return;
  return await track.noiseCancellation.enable();
}

export async function disableNoiseCancellation(track: TwilioLocalAudioTrack) {
  if (!track.noiseCancellation || !track.noiseCancellation.isEnabled) return;
  return await track.noiseCancellation.disable();
}

export function getLocalVideoTrackOpts(
  opts?: Pick<MediaMetadata, "deviceType" | "qualityMode"> & {
    source?: MediaSource.Camera | MediaSource.Screen;
  },
): TwilioCreateLocalTrackOptions {
  const {
    inputVideoDeviceId: deviceId,
    videoQualityMode: storedVideoQualityMode,
  } = getStoredAudioVideoSettings();
  const qualityMode: QualityMode =
    opts?.qualityMode ?? storedVideoQualityMode ?? QualityMode.High;
  const source = opts?.source ?? MediaSource.Camera;
  const deviceType = opts?.deviceType ?? getDeviceType();
  return {
    deviceId,
    ...audioVideoSettingsByQualityMode[qualityMode][deviceType].video[source],
    ...getDevTrackSettings().videoSettings,
  };
}

export async function createLocalVideoTrack(
  options?: TwilioCreateLocalTrackOptions,
) {
  return createTwilioLocalVideoTrack({
    ...getLocalVideoTrackOpts(),
    ...options,
  });
}

export async function createLocalAudioTrack(
  options?: TwilioCreateLocalTrackOptions,
) {
  return createTwilioLocalAudioTrack({
    ...getLocalAudioTrackOpts(),
    ...options,
  });
}

/**
 * Creates both the audio and video tracks as a batch operation. More efficient
 * than creating each individually.
 */
export async function createLocalMediaTracks() {
  return createTwilioLocalTracks({
    audio: getLocalAudioTrackOpts(),
    video: getLocalVideoTrackOpts(),
  });
}

/**
 * Unmounts a device from the browser; can no longer reuse the same track after
 * calling this.
 */
export function releaseTrack(
  track: TwilioLocalVideoTrack | TwilioLocalAudioTrack,
) {
  if (!track.isStopped) {
    track.stop();
  }
}

/**
 * Disables a local track, and unpublishes it if a local participant is provided.
 */
export function unpublishLocalTrack(
  track: LocalAVTrack,
  localParticipant?: TwilioLocalParticipant,
) {
  track.disable();

  if (localParticipant) {
    localParticipant.unpublishTrack(track);
  }

  return track;
}

/**
 * Enables a local track, and publishes it if a local participant is provided.
 */
export async function publishLocalTrack(
  track: LocalAVTrack,
  localParticipant?: TwilioLocalParticipant,
  isEnabled = true,
) {
  if (isEnabled) {
    track.enable();
  } else {
    track.disable();
  }

  if (localParticipant && isEnabled) {
    await localParticipant.publishTrack(track);
  }

  return track;
}

export async function getAvailableDevices() {
  const devices = await navigator.mediaDevices.enumerateDevices();

  return {
    audioInputDevices: devices.filter(
      (device) => device.kind === DeviceInputType.AUDIO_INPUT,
    ),
    videoInputDevices: devices.filter(
      (device) => device.kind === DeviceInputType.VIDEO_INPUT,
    ),
    audioOutputDevices: devices.filter(
      (device) => device.kind === DeviceInputType.AUDIO_OUTPUT,
    ),
  };
}
